/**
 * Signing in.
 *
 * ---------------------------------------------------------------------
 * THE LIGHT
 *
 * A member who cannot get in has two possible problems and no way to
 * tell them apart: they typed the wrong password, or the system is down.
 * One of those is theirs to fix and one is not, and guessing wrong
 * produces a telephone call to the club either way.
 *
 * So the card polls `/health/ready` — which is already public, already
 * checks the database, and carries nothing belonging to anybody — and
 * says which. It is a dot AND a word, not a colour alone: the whole
 * point is to be readable, including to somebody who cannot tell green
 * from amber.
 *
 * It never blocks the form. A check that has not answered yet must not
 * stop somebody typing, and one that says "degraded" must not stop them
 * trying anyway — the check can be wrong, and the password they are
 * about to type might work.
 *
 * ---------------------------------------------------------------------
 * FORGOTTEN PASSWORD, WITHOUT EMAIL
 *
 * Nothing in this platform sends email yet, so the honest flow is the
 * one a club already runs: ring the club, they issue a code, you type it
 * here with the password you want. The panel says exactly that rather
 * than offering a "send me a link" button that would do nothing.
 *
 * The code and the new password are on ONE form. Redeeming and then
 * being asked to sign in with the password you invented eight seconds
 * ago is a step that exists only because it was easier to write.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useSession } from '../lib/session.tsx';
import { api, ApiError, setToken } from '../lib/api.ts';
import { BetterClubLockup } from '../components/brand.tsx';

type Health = 'checking' | 'up' | 'degraded' | 'unreachable';

const HEALTH_LABEL: Record<Health, string> = {
  checking: 'Checking…',
  up: 'System available',
  degraded: 'System having trouble',
  unreachable: 'Cannot reach the system',
};

function SystemLight({ state }: { state: Health }) {
  return (
    <div className={`login-status login-status--${state}`}>
      <span className="login-status__dot" aria-hidden="true" />
      <span>{HEALTH_LABEL[state]}</span>
    </div>
  );
}

/**
 * Once on load, then every half minute.
 *
 * Not faster: this is a reassurance, not a monitor, and a login page
 * hammering a health endpoint is a login page that turns up in somebody's
 * rate limits.
 */
function useHealth(): Health {
  const [health, setHealth] = useState<Health>('checking');

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch('/health/ready');
        if (alive) setHealth(res.ok ? 'up' : 'degraded');
      } catch {
        // The fetch itself failed: no network, or nothing listening.
        // Distinct from a 503, which means the server answered and said
        // it is unwell — a different problem with a different fix, and
        // the member should be told which one they have.
        if (alive) setHealth('unreachable');
      }
    };
    check();
    const t = setInterval(check, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return health;
}

export function Login() {
  const { signIn, expired } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clubSlug, setClubSlug] = useState('');
  const [needsClub, setNeedsClub] = useState<{ slug: string; name: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'sign-in' | 'code'>('sign-in');
  const health = useHealth();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password, clubSlug || undefined);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'club_required') {
        // One address registered at more than one club. Ask rather than guess:
        // logging someone into the wrong club would be worse than a second step.
        const d = err.detail as { clubs: { slug: string; name: string }[] };
        setNeedsClub(d.clubs);
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not sign in.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'code') {
    return (
      <div className="login-page">
        <RedeemCard email={email} health={health} onBack={() => setMode('sign-in')} />
      </div>
    );
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        {/* Sign-in happens before we know which club this is, so the vendor
            mark leads here — it is the only screen in the app where it does. */}
        <h1 className="login-card__mark">
          <BetterClubLockup height={44} />
        </h1>
        <div className="tagline">YOUR CLUB, BETTER CONNECTED.</div>

        {/* Why they are looking at this card.
            Dropped here from the middle of a task, with no explanation,
            a person's first assumption is that the software lost their
            work — so say what happened, and say that it is not lost.
            Suppressed once they have a real error to read instead;
            two banners is one too many. */}
        {expired && !error && (
          <div className="login-notice">
            Your session ended, so we signed you out. Sign in again and you will
            come straight back to the screen you were on.
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            autoComplete="username"
            autoFocus
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {needsClub && (
          <label className="field">
            <span>Club</span>
            <select value={clubSlug} onChange={(e) => setClubSlug(e.target.value)} required>
              <option value="">Choose…</option>
              {needsClub.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <button type="submit" disabled={busy} style={{ width: '100%', marginTop: 6 }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="login-card__foot">
          <button
            type="button"
            className="linkish"
            onClick={() => {
              setError(null);
              setMode('code');
            }}
          >
            Forgotten your password?
          </button>
          <SystemLight state={health} />
        </div>
      </form>
    </div>
  );
}

/**
 * A code, and the password it buys.
 *
 * The one screen in the application that writes without a token. It
 * makes no promise it cannot keep: it does not offer to email anybody,
 * because nothing here can, and it says who to ask instead.
 */
function RedeemCard({
  email: initialEmail,
  health,
  onBack,
}: {
  email: string;
  health: Health;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [clubSlug, setClubSlug] = useState('');
  const [clubs, setClubs] = useState<{ slug: string; name: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = again !== '' && again !== password;
  const tooShort = password !== '' && password.length < 12;
  const ready =
    email.trim() !== '' &&
    code.trim().length >= 4 &&
    password.length >= 12 &&
    again === password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string }>('/v1/auth/redeem', {
        method: 'POST',
        body: {
          email: email.trim(),
          code: code.trim(),
          new_password: password,
          club_slug: clubSlug || undefined,
        },
      });
      setToken(res.token);
      // A full load rather than a router push: the session provider reads
      // the token once, at mount, and nothing else here knows how to tell
      // it that one has appeared.
      window.location.assign('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'club_required') {
        const d = err.detail as { clubs: { slug: string; name: string }[] };
        setClubs(d.clubs);
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not use that code.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="login-card" onSubmit={submit}>
      <h1 className="login-card__mark">
        <BetterClubLockup height={44} />
      </h1>
      <div className="tagline">YOUR CLUB, BETTER CONNECTED.</div>

      <p className="muted small" style={{ marginTop: 10, marginBottom: 12 }}>
        Ask the club office for a sign-in code — they can issue one while you are on the
        telephone. Type it here with the password you want, and you will be signed in.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <label className="field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          required
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      <label className="field">
        <span>Code from the club</span>
        {/* Uppercased and spaced out, because it was read down a telephone
            and is being typed by somebody not looking at the keyboard.
            The server strips spaces and hyphens and upper-cases anyway,
            so this is legibility rather than validation. */}
        <input
          value={code}
          style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}
          autoComplete="one-time-code"
          required
          onChange={(e) => setCode(e.target.value)}
        />
        <div className="hint">Ten characters. Hyphens and spaces do not matter.</div>
      </label>

      <label className="field">
        <span>New password</span>
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          required
          onChange={(e) => setPassword(e.target.value)}
        />
        {tooShort && <span className="muted small">At least twelve characters.</span>}
      </label>

      <label className="field">
        <span>New password again</span>
        <input
          type="password"
          value={again}
          autoComplete="new-password"
          required
          onChange={(e) => setAgain(e.target.value)}
        />
        {mismatch && <span className="muted small">These two do not match.</span>}
      </label>

      {clubs && (
        <label className="field">
          <span>Club</span>
          <select value={clubSlug} onChange={(e) => setClubSlug(e.target.value)} required>
            <option value="">Choose…</option>
            {clubs.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <button type="submit" disabled={busy || !ready} style={{ width: '100%', marginTop: 6 }}>
        {busy ? 'Signing you in…' : 'Set my password and sign in'}
      </button>

      <div className="login-card__foot">
        <button type="button" className="linkish" onClick={onBack}>
          Back to signing in
        </button>
        <SystemLight state={health} />
      </div>
    </form>
  );
}
